import React, { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Divider, Grid, IconButton, Typography } from '@mui/material';
import { AppButton } from '../../AppButton';
import AddIcon from '@mui/icons-material/Add';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RemoveIcon from '@mui/icons-material/Remove';
import { useStyles } from '../styles';
import { enqueueSnackbar } from 'notistack';
import RowContent from '../RowContent';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { EventFormJsonFields } from '../../../utils/interfaces';
import KenColors from '../../../utils/kenColors';

const ArrayFields: React.FunctionComponent<EventFormJsonFields> = (props) => {
  const { handleSubmit, control } = useFormContext();
  const { fields: arrayFields, append, remove } = useFieldArray({ name: props.metadata?.name as string, control: control });
  const classes = useStyles();

  useEffect(() => {
    append(props.defaultFields);
  }, []);

  const handleRemoveRecord = (key: number) => {
    if (arrayFields.length === 1) {
      return enqueueSnackbar({ message: 'Atlest one record is required', variant: 'error', autoHideDuration: 1000 });
    } else {
      remove(key);
    }
  };

  return arrayFields.map((items: any, index: number) => {
    const cardData = props.metadata?.fieldsToShow;
    switch (props.arrayVariant) {
      case 'variant1':
        return (
          <>
            {index === arrayFields.length - 1 ? (
              <Box
                width={'100%'}
                display={'flex'}
                component={'form'}
                onSubmit={handleSubmit((data) => {
                  alert(data);
                })}
                flexDirection={props.metadata?.actionAlingment === 'bottom' ? 'column-reverse' : 'column'}
                marginTop={index !== 0 && props.metadata?.removeMultiple ? '20px' : '0px'}
                key={items.id}
              >
                <Typography className={classes.scheduleHeader}>{props.title}</Typography>
                <Grid item container md={12} lg={12} spacing={1}>
                  {props.fieldsArray?.map((rows) => {
                    return (
                      <Grid item xs={rows.grid.xs} md={rows.grid.md} lg={rows.grid.lg}>
                        {rows.fields.map((fields) => {
                          fields['name'] = fields.dynamicName ? `${fields.dynamicName} ${index + 1}` : fields.name;
                          return <RowContent arrayIndex={index} fields={{ ...fields }} arrayName={`${props.metadata?.name}.${index}`} />;
                        })}
                      </Grid>
                    );
                  })}
                </Grid>
                <Box component={'div'} className="d-flex justify-content-between">
                  <AppButton
                    size="medium"
                    variant="contained"
                    fullWidth={false}
                    others={{ color: KenColors.ClientBlue9, borderRadius: '8px', padding: '12px 24px' }}
                    background={KenColors.ClientBlue4}
                    endIcon={<AddIcon color={KenColors.ClientBlue9 as any} />}
                    type="submit"
                  >
                    Save & another session
                  </AppButton>
                  <Box />
                </Box>
              </Box>
            ) : (
              <Box component={'div'} sx={{ background: KenColors.ClientGrey2, borderRadius: '12px', padding: '20px', margin: '10px 0px' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <Typography color={KenColors.ClientBlack2} fontSize={'16px'} fontWeight={'500'}>
                    Session {index + 1}
                  </Typography>
                  <div className="d-flex gap-3">
                    <IconButton>
                      <DeleteOutlineOutlinedIcon />
                    </IconButton>
                    <IconButton>
                      <EditOutlinedIcon />
                    </IconButton>
                  </div>
                </div>
                <Divider sx={{ margin: '15px 0px' }} />
                <Box component={'div'} className="d-flex" gap={'40px'}>
                  {cardData?.map((subItems: string, index: number) => {
                    const fieldArray = props?.fieldsArray || [];
                    let currentField;
                    for (let i = 0; i < fieldArray.length; i++) {
                      const fields = fieldArray[i].fields || [];
                      for (let i = 0; i < fields.length; i++) {
                        const field = fields[i];
                        if (field.name === subItems) {
                          currentField = field;
                        }
                      }
                    }
                    const fieldShowValue = items[subItems] || '-';
                    return (
                      items[subItems] && (
                        <Box component={'div'} className="d-flex flex-column" gap={'6px'} key={index}>
                          <Typography color={KenColors.ClientGrey} fontSize={'14px'} fontWeight={'500'}>
                            {currentField?.fieldToShow?.label || ''}
                          </Typography>
                          <Typography color={KenColors.ClientBlack2} fontSize={'16px'} fontWeight={'500'}>
                            {fieldShowValue}
                          </Typography>
                        </Box>
                      )
                    );
                  })}
                </Box>
              </Box>
            )}
          </>
        );
      default:
        return (
          <Box
            width={'100%'}
            display={'flex'}
            flexDirection={props.metadata?.actionAlingment === 'bottom' ? 'column-reverse' : 'column'}
            marginTop={index !== 0 && props.metadata?.removeMultiple ? '20px' : '0px'}
            key={items.id}
          >
            <div className={classes.scheduleHeaderContainer}>
              <div>
                {index === 0 ? (
                  <>
                    {props?.title && <Typography className={classes.scheduleHeader}>{props.title}</Typography>}
                    {props?.subTitle && <Typography className={classes.scheduleHeaderSubTitle}>{props.subTitle}</Typography>}
                  </>
                ) : props.metadata?.removeMultiple && index === arrayFields.length - 1 ? (
                  <AppButton
                    width="100px"
                    background={KenColors.ClientPrimaryGrey}
                    variant="contained"
                    className={classes.arrayButton}
                    size="small"
                    startIcon={<RemoveIcon />}
                    onClick={() => {
                      handleRemoveRecord(index);
                    }}
                  >
                    Remove
                  </AppButton>
                ) : null}
                {props?.metadata?.removeNeeded && (
                  <AppButton
                    variant="contained"
                    size="small"
                    others={{ margin: '10px 0px' }}
                    className={classes.arrayButton2}
                    endIcon={<CloseRoundedIcon />}
                    background={KenColors.ClientBlue4}
                    onClick={() => {
                      handleRemoveRecord(index);
                    }}
                  >
                    Speaker {index + 1}
                  </AppButton>
                )}
              </div>
              {props.metadata?.removeMultiple && (
                <>
                  {index + 1 === arrayFields.length ? (
                    <AppButton
                      width="100px"
                      background={KenColors.ClientPrimaryGrey}
                      variant="contained"
                      size="small"
                      className={classes.arrayButton}
                      startIcon={<AddIcon />}
                      onClick={() => {
                        append(props.defaultFields);
                      }}
                    >
                      Add more
                    </AppButton>
                  ) : (
                    <AppButton
                      width="100px"
                      background={KenColors.ClientPrimaryGrey}
                      variant="contained"
                      className={classes.arrayButton}
                      size="small"
                      startIcon={<RemoveIcon />}
                      onClick={() => {
                        handleRemoveRecord(index);
                      }}
                    >
                      Remove
                    </AppButton>
                  )}
                </>
              )}
            </div>
            <Grid item container md={12} lg={12} spacing={1}>
              {props.fieldsArray?.map((rows) => {
                return (
                  <Grid item xs={rows.grid.xs} md={rows.grid.md} lg={rows.grid.lg}>
                    {rows.fields.map((fields) => {
                      fields['name'] = fields.dynamicName ? `${fields.dynamicName} ${index + 1}` : fields.name;
                      return <RowContent arrayIndex={index} fields={{ ...fields }} arrayName={`${props.metadata?.name}.${index}`} />;
                    })}
                  </Grid>
                );
              })}
            </Grid>
            {!props.metadata?.removeMultiple && index === arrayFields.length - 1 && (
              <Box display={'flex'} flexDirection={'row-reverse'} justifyContent={'space-between'}>
                <Box />
                <AppButton
                  width="100px"
                  background={KenColors.ClientPrimaryGrey}
                  variant="contained"
                  size="small"
                  className={classes.arrayButton}
                  startIcon={<AddIcon />}
                  onClick={() => {
                    append(props.defaultFields);
                  }}
                >
                  Add more
                </AppButton>
              </Box>
            )}
          </Box>
        );
    }
  });
};

export default ArrayFields;
